import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { X } from 'react-feather';
import config from '../../../config';
import '../tenant-portal.css';

// Concessionaire that does NOT capture gross/tax/discount/return splits.
const NO_SPLIT_CONCESSIONAIRE = '68145733211db7ab0824a5db';

/**
 * Tenant manual sales-entry modal (V3). Lets a tenant fill the day's sales for
 * a store whose `tenant_manual_flag` is on.
 *
 * The submit path is preserved verbatim from the legacy HomeView: it posts to
 * the public ingestion endpoints with the store's **x-api-key** (NOT the JWT) —
 * non-F&B → /salesTransactions, F&B → /deliverypartnersales — with optional
 * gross/tax/discount/return and the count-0-implies-sales-0 validation.
 *
 * Props:
 *   open            — bool
 *   transaction     — the daily row being edited (or null)
 *   deliveryPartners— ordered partner names (DineIn first); empty = non-F&B
 *   store           — { concessionaireCode, tenantCode, unitCode, mallCode, apiKey, concessionaireId }
 *   onClose         — () => void
 *   onSaved         — () => void  (refetch daily + close)
 */
const TenantSalesEntryModal = ({ open, transaction, deliveryPartners = [], store = {}, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [tx, setTx] = useState(transaction || null);
    const [saving, setSaving] = useState(false);

    // Re-seed the local editable copy whenever a new row is opened.
    useEffect(() => { setTx(transaction || null); }, [transaction]);

    if (!open || !tx) return null;

    const isFB = deliveryPartners.length > 0;
    const showSplits = store.concessionaireId !== NO_SPLIT_CONCESSIONAIRE;
    const set = (patch) => setTx((prev) => ({ ...prev, ...patch }));

    const submit = async (e) => {
        e.preventDefault();

        // ── Validation: a 0 count cannot carry non-zero sales ──────────────
        const mainCount = Number(tx.transaction_count) || 0;
        const mainSales = Number(tx.total_sales) || 0;
        if (mainCount === 0 && mainSales !== 0) {
            toast.warning(t('If the transaction count is 0, the net sales must be 0.'));
            return;
        }
        for (const partner of deliveryPartners) {
            const k = partner.toLowerCase().replace(/ /g, '_');
            const c = Number(tx[`${k}_count`]) || 0;
            const s = Number(tx[`${k}_sales`]) || 0;
            if (c === 0 && s !== 0) {
                toast.warning(t('For {{partner}}, if the count is 0, the sales must be 0.', { partner: partner.replace('_', ' ') }));
                return;
            }
        }

        const [year, month, day] = tx.transaction_date.split('-');
        const formattedDate = new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString();

        const gross = Number(tx.gross_sales);
        const tax = Number(tx.tax_amount);
        const discount = Number(tx.discount_amount);
        const ret = Number(tx.return_amount);
        const addOptional = (target) => {
            if (tx.gross_sales !== null && tx.gross_sales !== '' && !Number.isNaN(gross)) target.gross_amount = gross;
            if (tx.tax_amount !== null && tx.tax_amount !== '' && !Number.isNaN(tax)) target.tax_amount = tax;
            if (tx.discount_amount !== null && tx.discount_amount !== '' && !Number.isNaN(discount)) target.discount_amount = discount;
            if (tx.return_amount !== null && tx.return_amount !== '' && !Number.isNaN(ret)) target.return_amount = ret;
        };

        // The ingestion endpoints authenticate with the store API key, not the JWT.
        const authorization = { headers: { 'x-api-key': store.apiKey } };
        const base = {
            concessionaire_code: store.concessionaireCode,
            tenant_code: store.tenantCode,
            outlet_code: store.unitCode,
            mall_code: store.mallCode,
            transaction_date: formattedDate,
        };

        setSaving(true);
        try {
            if (!isFB) {
                const data = { ...base, transaction_count: tx.transaction_count, net_sales: tx.total_sales };
                addOptional(data);
                await axios.post(`${config.hostname}/salesTransactions`, data, authorization);
            } else {
                const data = { ...base };
                addOptional(data);
                deliveryPartners.forEach((partner) => {
                    const k = partner.toLowerCase().replace(/ /g, '_');
                    data[`${k}_sales`] = tx[`${k}_sales`] || 0;
                    data[`${k}_count`] = tx[`${k}_count`] || 0;
                });
                await axios.post(`${config.hostname}/deliverypartnersales`, data, authorization);
            }
            toast.success(t('Data updated successfully'));
            onSaved?.();
        } catch (err) {
            console.error('manual sales entry failed', err);
            toast.error(t('Error'));
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="v3-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="v3-modal v3-card" role="dialog" aria-modal="true">
                <div className="v3-modal-header">
                    <h3 className="v3-title">{t('Update Sales Data')}</h3>
                    <button type="button" className="v3-modal-close" onClick={onClose} aria-label={t('Close')}>
                        <X size={16} />
                    </button>
                </div>

                <form className="v3-modal-body" onSubmit={submit}>
                    <div className="v3-report-field">
                        <span className="v3-label">{t('Transaction Date')}</span>
                        <input className="v3-input" type="text" value={tx.transaction_date} disabled />
                    </div>

                    {!isFB && (
                        <div className="v3-sadmin-form-grid-3">
                            <div className="v3-report-field">
                                <label className="v3-label">{t('Transaction Count')}</label>
                                <input className="v3-input" type="number" required value={tx.transaction_count}
                                    onChange={(e) => set({ transaction_count: e.target.value })}
                                    onWheel={(e) => e.currentTarget.blur()} />
                            </div>
                            <div className="v3-report-field">
                                <label className="v3-label">{t('Net Sales')}</label>
                                <input className="v3-input" type="number" required value={tx.total_sales}
                                    onChange={(e) => set({ total_sales: e.target.value })}
                                    onWheel={(e) => e.currentTarget.blur()} />
                            </div>
                        </div>
                    )}

                    {isFB && (
                        <div className="v3-card v3-fb-split">
                            <span className="v3-label">{t('F&B Sales Split')}</span>
                            <div className="v3-sadmin-table-wrap">
                                <table className="v3-sadmin-table">
                                    <thead>
                                        <tr>
                                            <th>{t('Delivery Partner')}</th>
                                            <th className="num">{t('Transaction Count')}</th>
                                            <th className="num">{t('Net Sales')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveryPartners.map((partner) => {
                                            const k = partner.toLowerCase().replace(/ /g, '_');
                                            return (
                                                <tr key={k}>
                                                    <td>{partner.replace('_', ' ')}</td>
                                                    <td className="num">
                                                        <input className="v3-input" type="number" required value={tx[`${k}_count`] ?? ''}
                                                            onChange={(e) => set({ [`${k}_count`]: e.target.value })}
                                                            onWheel={(e) => e.currentTarget.blur()} />
                                                    </td>
                                                    <td className="num">
                                                        <input className="v3-input" type="number" required value={tx[`${k}_sales`] ?? ''}
                                                            onChange={(e) => set({ [`${k}_sales`]: e.target.value })}
                                                            onWheel={(e) => e.currentTarget.blur()} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {showSplits && (
                        <div className="v3-sadmin-form-grid-3">
                            {[
                                ['gross_sales', 'Gross Sales'],
                                ['tax_amount', 'Tax Amount'],
                                ['discount_amount', 'Discount Amount'],
                                ['return_amount', 'Return Amount'],
                            ].map(([key, label]) => (
                                <div className="v3-report-field" key={key}>
                                    <label className="v3-label">{t(label)}</label>
                                    <input className="v3-input" type="number" value={tx[key] ?? ''}
                                        onChange={(e) => set({ [key]: e.target.value })}
                                        onWheel={(e) => e.currentTarget.blur()} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="v3-modal-footer">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose}>{t('Close')}</button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            {saving ? t('Updating…') : t('Update Sales')}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
};

export default TenantSalesEntryModal;
